import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { APIProvider } from '@vis.gl/react-google-maps';
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Typography,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale';

import PlaceAutocomplete from '../common/PlaceAutocomplete';
import RoutePointsEditor, { reorderRoutePoints, useRoutePointShortcuts } from '../rides/RoutePointsEditor';
import { MAX_INTERMEDIATE_STOPS, buildStopFields } from '../../utils/rideStops';
import { VEHICLE_TYPE_OPTIONS } from '../../config/vehicleTypes';
import { createRide, fetchRideTemplates } from '../../redux/slices/ridesSlice';
import { fetchCustomers } from '../../redux/slices/customersSlice';
import { useRideDialog } from '../rides/RideDialogProvider';
import DispatchInputHelpDialog from '../rides/DispatchInputHelpDialog';
import useFormKeyboardNav from '../../hooks/useFormKeyboardNav';
import { NO_AUTOFILL, createFieldIdFactory } from '../../utils/noAutofill';
import { filterSelectableDrivers } from '../../utils/driverActive';
import { geocodePreciseAddress, hasCoordinates } from '../../utils/addressGeocoding';
import { getServiceAreaBias } from '../../utils/serviceAreaBias';
import { phoneRecognitionHint } from '../../utils/phoneNumber';

// Deutlich sichtbarer Fokusrahmen: Tastaturbedienung funktioniert nur, wenn
// jederzeit erkennbar ist, wo der Fokus steht (Konzept 3.8).
//
// Kein pauschales `:focus-visible`-Outline auf allen Feldern: MUI zeichnet die
// Beschriftung in eine Aussparung der Feldumrandung, und ein zweiter Rahmen um
// das innere <input> läuft genau durch diese Beschriftung hindurch. Stattdessen
// wird die vorhandene Umrandung verstärkt – die Aussparung bleibt dabei erhalten.
const focusVisibleSx = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 2,
    borderColor: 'primary.main',
  },
  // Beschriftung mitfärben, damit der Fokus auch bei schmalen Feldern auffällt.
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'primary.main',
    fontWeight: 600,
  },
  // Buttons tragen keine Beschriftung im Rahmen – hier ist ein Outline richtig.
  '& .MuiButtonBase-root:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '2px',
  },
  // Googles Web Component bringt kein MUI-Styling mit und hat keine
  // Aussparung, durch die etwas laufen könnte.
  '& gmp-place-autocomplete:focus-within input': {
    outline: '2px solid #1976d2',
    outlineOffset: '1px',
  },
};

/**
 * Super-kompaktes "Neue Fahrt"-Formular für das LiveMap-Fahrten-Panel (~420px).
 * Dient dem schnellen Disponieren von Telefonbestellungen.
 *
 * Props:
 *  - initialDraft: { customerName, customerPhone, pickupAddress, incomingCallSessionId }
 *  - onClose:      schließt das Formular (zurück zur Fahrtenliste)
 *  - onCreated:    Callback nach erfolgreichem Anlegen (erhält die erstellte Fahrt)
 */
const QuickRideForm = ({ initialDraft, expandRef, helpRef, onClose, onCreated }) => {
  const dispatch = useDispatch();
  const { openRideDialog } = useRideDialog() || {};
  // Fahrer kommen aus dem AppDataManager (Live-Listener) – dieselbe Quelle, aus
  // der die LiveMap ihre Marker speist. `state.drivers` wird nur von Formularen
  // befüllt, die selbst fetchDrivers() auslösen; hier wäre die Liste sonst leer.
  const appDataDrivers = useSelector((state) => state.appData.drivers);
  const fetchedDrivers = useSelector((state) => state.drivers.drivers);
  const drivers = appDataDrivers?.length ? appDataDrivers : fetchedDrivers;
  const vehicles = useSelector((state) => state.appData.vehicles);
  const templates = useSelector((state) => state.rides.templates);
  const { customers } = useSelector((state) => state.customers);

  // Vorlagen laden (für das Dropdown, falls der Kunde welche hat).
  useEffect(() => {
    dispatch(fetchRideTemplates());
  }, [dispatch]);

  // Kundenliste für die Namensvorschläge – dieselbe Quelle wie im großen
  // Formular, damit der Disponent hier nicht schlechter dasteht.
  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  // Feldkennung wechselt pro Öffnen, damit der Browser keine Eingaben der
  // letzten Fahrt vorschlägt (docs 3.8).
  const fieldIdRef = useRef(null);
  if (!fieldIdRef.current) fieldIdRef.current = createFieldIdFactory();
  const fieldId = fieldIdRef.current;

  const [templateId, setTemplateId] = useState('');
  const [mode, setMode] = useState('sofort'); // 'sofort' | 'vorbestellung'
  const [scheduledTime, setScheduledTime] = useState(() => new Date(Date.now() + 60 * 60 * 1000));

  const [name, setName] = useState(initialDraft?.customerName || '');
  // Wem das Namensfeld gerade zugeordnet ist – aus der Anrufererkennung oder
  // aus den Vorschlägen. Steuert, welche Vorlagen zur Auswahl stehen.
  const [customerId, setCustomerId] = useState(initialDraft?.customerId || null);
  const [phone, setPhone] = useState(initialDraft?.customerPhone || '');
  const [pickupAddress, setPickupAddress] = useState(initialDraft?.pickupAddress || '');
  const [pickupLoc, setPickupLoc] = useState(null); // { latitude, longitude }
  const [destAddress, setDestAddress] = useState('');
  const [destLoc, setDestLoc] = useState(null);
  // Zwischenstopps (zwischenstopps_konzept.md §3.2): { address, location }
  const [stops, setStops] = useState([]);
  const [vehicleType, setVehicleType] = useState(1);
  const [driverId, setDriverId] = useState('');
  const [notes, setNotes] = useState('');

  // Adresstexte, zu denen die hinterlegten Koordinaten gehören (analog
  // RideForm): Nach einer Auswahl meldet das Google-Element den Text selbst
  // noch einmal als Eingabe – nur ein abweichender Text ist von Hand getippt.
  const selectedAddressRef = useRef({ pickup: null, destination: null });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  // Beim Schließen der Hilfe soll der Disponent exakt dort weitertippen, wo er
  // aufgehört hat – inklusive Cursorposition. MUI stellt nur den Fokus wieder
  // her, nicht die Auswahl im Feld.
  const helpReturnRef = useRef(null);

  const openHelp = () => {
    const active = document.activeElement;
    helpReturnRef.current =
      active && typeof active.setSelectionRange === 'function'
        ? { element: active, start: active.selectionStart, end: active.selectionEnd }
        : { element: active, start: null, end: null };
    setHelpOpen(true);
  };

  const closeHelp = () => {
    setHelpOpen(false);
    const saved = helpReturnRef.current;
    helpReturnRef.current = null;
    if (!saved?.element?.isConnected) return;
    // Nach dem Schließen der Dialog-Transition, sonst zieht MUI den Fokus zurück.
    setTimeout(() => {
      saved.element.focus();
      if (saved.start !== null) {
        try {
          saved.element.setSelectionRange(saved.start, saved.end);
        } catch (e) {
          // Manche Feldtypen erlauben setSelectionRange nicht – Fokus genügt.
        }
      }
    }, 0);
  };

  // Enter-springt-weiter ist abschaltbar (Konzept 3.3). Der Settings-Schalter
  // folgt in einem späteren Schritt; bis dahin gilt der Default.
  const enterAdvances = useSelector(
    (state) => state.settings?.global?.keyboardEnterAdvances ?? true
  );

  // Betriebsgebiet als weicher Bias, wenn eine getippte Adresse nachträglich
  // aufgelöst werden muss – dieselbe Quelle wie die Adressvorschläge.
  const globalSettings = useSelector((state) => state.settings.global);
  const serviceAreaBounds = useMemo(
    () => getServiceAreaBias(globalSettings)?.bounds || null,
    [globalSettings]
  );

  // Verfügbare Fahrer (analog RideForm): Sofort → online mit Fahrzeug; Vorbestellung → alle.
  // Deaktivierte Fahrer bleiben in beiden Fällen außen vor; hier wird immer eine
  // neue Fahrt angelegt, es gibt also keinen bereits zugewiesenen Fahrer zu halten.
  const availableDrivers = useMemo(() => {
    const selectable = filterSelectableDrivers(drivers);
    if (selectable.length === 0) return [];
    if (mode === 'vorbestellung') return selectable;
    return selectable.filter((d) => d.isOnline && (d.vehicle || d.car));
  }, [drivers, mode]);

  // Vorlagen des (gematchten) Kunden – nur bei bekanntem Kunden aus dem Anruf.
  const customerTemplates = useMemo(
    () => (customerId ? (templates || []).filter((t) => t.customer_id === customerId) : []),
    [templates, customerId]
  );

  const driverLabel = (d) => d?.name || d?.user?.name || 'Unbekannt';

  // Fahrzeugnummer je Fahrer – Zuordnung wie in der LiveMap: die Vehicles-
  // Sammlung ist die führende Quelle, das eingebettete `car` des Fahrers kann
  // nach einem Fahrzeugwechsel veraltet sein.
  const vehicleByDriverId = useMemo(() => {
    return (vehicles || []).reduce((map, vehicle) => {
      const id = vehicle.driverId || vehicle.current_driver?.id || vehicle.current_driver_id || null;
      if (id) map[id] = vehicle;
      return map;
    }, {});
  }, [vehicles]);

  const driverVehicleNumber = (d) => {
    if (!d) return '';
    const assigned = vehicleByDriverId[d.id] || d.vehicle || null;
    return (assigned?.vehicleNumber || d.car?.vehicleNumber || d.vehicleNumber || '')
      .toString()
      .trim();
  };

  // Kundendaten liegen je nach Datensatz direkt oder unter `user`.
  const customerName = (c) => c?.name || c?.user?.name || '';
  const customerPhone = (c) =>
    c?.phone || c?.phoneNumber || c?.user?.phoneNumber || '';

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = customerTemplates.find((x) => x.id === id);
    if (!t) return;
    setPickupAddress(t.pickup_address || '');
    setPickupLoc(t.pickup_location || null);
    setDestAddress(t.destination_address || '');
    setDestLoc(t.destination_location || null);
    setVehicleType(Number(t.vehicle_type) || 1);
    // Dauerauftrag (order_type 3) kann die Kompaktansicht nicht abbilden → Sofort.
    setMode(String(t.order_type) === '1' ? 'vorbestellung' : 'sofort');
  };


  const handleSubmit = async () => {
    if (!pickupAddress.trim()) {
      setError('Bitte eine Abholadresse angeben.');
      return;
    }
    if (mode === 'vorbestellung' && !scheduledTime) {
      setError('Bitte einen Zeitpunkt für die Vorbestellung angeben.');
      return;
    }

    // Beim Tippen werden die Koordinaten verworfen (siehe onChange der
    // Adressfelder). Wer keinen Vorschlag anklickt, hätte sonst eine Fahrt mit
    // (0,0) angelegt: keine Route auf der LiveMap, keine brauchbare Schätzung.
    setSubmitting(true);
    setError(null);
    let resolvedPickupLoc = pickupLoc;
    let resolvedDestLoc = destLoc;
    const resolvedStops = [];
    try {
      if (!hasCoordinates(resolvedPickupLoc)) {
        const geocoded = await geocodePreciseAddress(pickupAddress, { bounds: serviceAreaBounds });
        if (!geocoded) {
          setError('Abholadresse ist nicht eindeutig — bitte einen Vorschlag aus der Liste wählen oder auf der Karte festlegen.');
          return;
        }
        resolvedPickupLoc = { latitude: geocoded.latitude, longitude: geocoded.longitude };
        setPickupLoc(resolvedPickupLoc);
      }
      if (destAddress.trim() && !hasCoordinates(resolvedDestLoc)) {
        const geocoded = await geocodePreciseAddress(destAddress, { bounds: serviceAreaBounds });
        if (!geocoded) {
          setError('Zieladresse ist nicht eindeutig — bitte einen Vorschlag aus der Liste wählen oder auf der Karte festlegen.');
          return;
        }
        resolvedDestLoc = { latitude: geocoded.latitude, longitude: geocoded.longitude };
        setDestLoc(resolvedDestLoc);
      }
      // Zwischenstopps genauso: ohne echte Koordinaten verböge ein Halt die
      // Route und landete als (0,0) in der Fahrt. Leere Felder zählen nicht.
      for (const [i, stop] of stops.entries()) {
        const address = (stop.address || '').trim();
        if (!address) continue;
        let location = stop.location;
        if (!hasCoordinates(location)) {
          const geocoded = await geocodePreciseAddress(address, { bounds: serviceAreaBounds });
          if (!geocoded) {
            setError(`Zwischenstopp ${i + 1} ist nicht eindeutig — bitte einen Vorschlag aus der Liste wählen oder auf der Karte festlegen.`);
            return;
          }
          location = { latitude: geocoded.latitude, longitude: geocoded.longitude };
        }
        resolvedStops.push({ address, location });
      }
      if (resolvedStops.length > 0) {
        setStops(resolvedStops);
        // Das Endziel ist der letzte Halt – ohne Ziel keine Halte-Kette.
        if (!destAddress.trim()) {
          setError('Bei Fahrten mit Zwischenstopp bitte auch eine Zieladresse angeben.');
          return;
        }
      }
    } finally {
      // Der eigentliche Anlege-Vorgang setzt das Flag gleich wieder.
      setSubmitting(false);
    }

    const orderType = mode === 'vorbestellung' ? 1 : 2;
    let status;
    if (driverId) {
      status = orderType === 1 ? 'pre_accepted' : 'dispatcher_pending';
    } else {
      status = orderType === 1 ? 'scheduled' : 'open';
    }

    const selectedDriver = driverId ? availableDrivers.find((d) => d.id === driverId) : null;
    // Fahrzeug liegt je nach Quelle als aufgelöster Datensatz (`vehicle`, aus
    // fetchDrivers) oder als eingebettetes Feld des Nutzerdokuments (`car`) vor.
    const selectedDriverVehicle = selectedDriver?.vehicle || selectedDriver?.car || null;

    // Ist ein Kunde zugeordnet, haengt die Fahrt an seinem Profil. Nur ohne
    // Zuordnung entsteht ein Laufkunde. Das Telefonkunden-Flag kommt dann aus
    // dem Kundendatensatz selbst – ein App-Kunde ist keiner (analog RideForm).
    const selectedCustomer = customerId
      ? (customers || []).find((c) => c.id === customerId)
      : null;

    const payload = {
      customerId: selectedCustomer ? selectedCustomer.id : null,
      customerName: name.trim() || 'Laufkunde',
      customerPhone: phone.trim() || null,
      isPhoneCustomer: selectedCustomer
        ? selectedCustomer.user?.isPhoneCustomer || false
        : true,
      start: pickupAddress,
      startLatitude: resolvedPickupLoc?.latitude ?? 0,
      startLongitude: resolvedPickupLoc?.longitude ?? 0,
      destination: destAddress || '',
      destinationLatitude: resolvedDestLoc?.latitude ?? 0,
      destinationLongitude: resolvedDestLoc?.longitude ?? 0,
      // Zwischenstopps (§2.1): `stops` plus gespiegeltes Endziel. Ohne Halte
      // liefert buildStopFields null-Felder – wie bisher ohne Zwischenstopp.
      ...buildStopFields({
        intermediateStops: resolvedStops.map((stop) => ({
          address: stop.address,
          latitude: stop.location?.latitude,
          longitude: stop.location?.longitude,
        })),
        destinationAddress: destAddress,
        destinationLocation: resolvedDestLoc,
      }),
      vehicleType: Number(vehicleType),
      orderType,
      status,
      notes: notes.trim() || null,
      ...(orderType === 1 ? { scheduledTime } : {}),
      ...(selectedDriver
        ? {
            driverId: selectedDriver.id,
            driverName: selectedDriver.name || selectedDriver.user?.name || 'Unbekannt',
            driverProfilePic:
              selectedDriver.profilePic || selectedDriver.user?.profile_picture || '',
            driverRating: selectedDriver.rating || null,
            driverRatingCount: selectedDriver.ratingCount || null,
            assignedByDispatcher: true,
            ...(orderType === 1
              ? { acceptedAt: new Date().toISOString() }
              : { is_active: false }),
            // Fahrzeugdaten nur bei Sofortbestellungen (bei Vorbestellung setzt die App sie später).
            ...(orderType === 2 && selectedDriverVehicle
              ? {
                  driverVehicleId:
                    selectedDriverVehicle.id || selectedDriverVehicle.vehicleId || null,
                  driverModel: selectedDriverVehicle.model || null,
                  driverLicensePlate: selectedDriverVehicle.licensePlate || null,
                }
              : {}),
          }
        : {}),
      ...(initialDraft?.incomingCallSessionId
        ? {
            incomingCallSessionId: initialDraft.incomingCallSessionId,
            bookingSource: 'incoming_call',
          }
        : {}),
    };

    setSubmitting(true);
    setError(null);
    try {
      const result = await dispatch(createRide(payload));
      if (!result.error) {
        onCreated?.(result.payload);
        onClose?.();
      } else {
        setError('Fahrt konnte nicht erstellt werden.');
      }
    } catch (e) {
      setError('Fahrt konnte nicht erstellt werden.');
    } finally {
      setSubmitting(false);
    }
  };

  // Übergibt die bereits eingegebenen Daten an das große RideForm-Dialogformular,
  // damit dort weitere Optionen ausgefüllt werden können. Schließt das Kompaktformular.
  // `asRecurring` (Alt+D): gleich als Dauerauftrag öffnen – den gibt es nur im
  // großen Formular, dort liegt er ebenfalls auf Alt+D.
  const handleExpand = ({ asRecurring = false } = {}) => {
    const prefill = {
      // Ausgewählten Kunden mitnehmen, damit das große Formular ihn nicht
      // erneut suchen lässt.
      customer_id: customerId || '',
      customer_name: name,
      customer_phone: phone,
      pickup_address: pickupAddress,
      pickup_location: pickupLoc,
      destination_address: destAddress,
      destination_location: destLoc,
      stops: stops.map((stop) => ({
        address: stop.address || '',
        location: stop.location || { latitude: null, longitude: null },
      })),
      vehicle_type: String(vehicleType),
      order_type: asRecurring ? '3' : (mode === 'vorbestellung' ? '1' : '2'),
      notes,
      driver_id: driverId || '',
      ...(mode === 'vorbestellung' && !asRecurring ? { scheduled_time: scheduledTime } : {}),
    };
    openRideDialog?.({
      mode: 'create',
      draft: {
        prefill,
        // Aus der Anruferkennung geöffnet: Die Verknüpfung zum Anruf muss mit,
        // sonst entsteht die Fahrt im großen Formular ohne `incomingCallSessionId`.
        ...(initialDraft?.incomingCallSessionId
          ? { incomingCallSessionId: initialDraft.incomingCallSessionId }
          : {}),
      },
    });
    onClose?.();
  };

  // Handler für den "Mehr Optionen"-Button im Panel-Header (LiveMap) registrieren.
  // Zuweisung bei jedem Render → verweist stets auf den aktuellen State-Closure.
  if (expandRef) {
    expandRef.current = () => handleExpand();
  }
  // Info-Symbol im Panel-Header (LiveMap) öffnet dieselbe Hilfe wie F1.
  if (helpRef) {
    helpRef.current = openHelp;
  }

  const canSubmit = !submitting && !!pickupAddress.trim();

  // Routenpunkte umsortieren (Drag & Drop oder Alt+↑/↓). Die Felder bleiben an
  // ihrer Position, nur Adressen samt Koordinaten wandern (RoutePointsEditor).
  const handleReorderRoutePoints = (fromIndex, toIndex) => {
    const points = reorderRoutePoints([
      { address: pickupAddress, location: pickupLoc },
      ...stops,
      { address: destAddress, location: destLoc },
    ], fromIndex, toIndex);
    const first = points[0];
    const last = points[points.length - 1];
    // Übernommene Adressen gelten als ausgewählt, nicht als getippt – sonst
    // verwürfe die nächste Echo-Eingabe des Google-Felds die Koordinaten.
    selectedAddressRef.current.pickup = first.address;
    selectedAddressRef.current.destination = last.address;
    setPickupAddress(first.address || '');
    setPickupLoc(first.location || null);
    setDestAddress(last.address || '');
    setDestLoc(last.location || null);
    setStops(points.slice(1, -1).map((point) => ({
      address: point.address || '',
      location: point.location || null,
    })));
  };

  const routeShortcuts = useRoutePointShortcuts({
    stopsAllowed: true,
    stopsCount: stops.length,
    maxStops: MAX_INTERMEDIATE_STOPS,
    onAddStop: () => setStops((prev) => [...prev, { address: '', location: null }]),
    onRemoveStop: (index) => setStops((prev) => prev.filter((_, i) => i !== index)),
    onReorder: handleReorderRoutePoints,
  });

  const { containerProps, focusField, focusNext } = useFormKeyboardNav({
    enterAdvances,
    onSubmit: handleSubmit,
    canSubmit,
    // Strg+Enter darf nicht wirkungslos verpuffen, wenn der Button deaktiviert
    // ist – der Button ist dann auch nicht fokussierbar (Konzept 3.7).
    onSubmitBlocked: () => {
      if (!pickupAddress.trim()) {
        setError('Bitte eine Abholadresse angeben.');
        focusField('pickupAddress');
      }
    },
    onEscape: () => onClose?.(),
    onHelp: openHelp,
    altShortcuts: {
      s: () => changeMode('sofort'),
      v: () => changeMode('vorbestellung'),
      1: () => setVehicleType(1),
      2: () => setVehicleType(2),
      3: () => setVehicleType(3),
      4: () => setVehicleType(4),
      // Ins große Formular: „Mehr Optionen" bzw. direkt als Dauerauftrag
      m: () => handleExpand(),
      d: () => handleExpand({ asRecurring: true }),
      // Zwischenstopps: Alt+Z / Alt+Entf / Alt+↑↓ (RoutePointsEditor)
      ...routeShortcuts.shortcuts,
    },
  });
  routeShortcuts.setFocusField(focusField);

  // Nach dem Wechsel muss der Fokus nachgeführt werden – das Zeitfeld gibt es
  // erst im nächsten Render, und beim Zurückschalten verschwindet es unter dem
  // Fokus. Beides kann erst nach dem Rendern passieren (siehe useEffect unten).
  const pendingModeFocusRef = useRef(null);

  const changeMode = (next) => {
    if (!next) return;

    if (next === mode) {
      // Alt+V bei bereits aktiver Vorbestellung: Das Feld steht schon, also
      // direkt hinspringen statt gar nichts zu tun.
      if (next === 'vorbestellung') focusField('scheduledTime');
      return;
    }

    if (next === 'vorbestellung') {
      // Direkt weiter zur Abholzeit – das ist die einzige Angabe, die eine
      // Vorbestellung von einer Sofortfahrt unterscheidet.
      pendingModeFocusRef.current = 'scheduledTime';
    } else {
      // Zurück auf Sofort: Das Zeitfeld wird entfernt. Stand der Fokus darin,
      // landet er sonst im Nichts und Tab/Enter beginnen wieder von vorn.
      const focusedInScheduled = !!document.activeElement?.closest?.(
        '[data-nav="scheduledTime"]'
      );
      pendingModeFocusRef.current = focusedInScheduled ? 'name' : null;
    }

    setMode(next);
    // Fahrerauswahl zurücksetzen, da sich die verfügbare Menge ändert.
    setDriverId('');
  };

  const handleModeChange = (_e, next) => changeMode(next);

  // Fokus nach einem Moduswechsel nachführen, sobald das Zeitfeld im DOM ist
  // bzw. verschwunden ist.
  useEffect(() => {
    const target = pendingModeFocusRef.current;
    if (!target) return;
    pendingModeFocusRef.current = null;
    focusField(target);
  }, [mode, focusField]);

  // Nach der Auswahl in einem Select automatisch weiter – erspart den
  // zusätzlichen Tab (Konzept 3.6).
  //
  // Der Sprung darf erst laufen, wenn das Menü vollständig geschlossen ist:
  // MUI gibt den Fokus danach an den Select zurück und würde einen früheren
  // Sprung wieder einkassieren. `onExited` der Menü-Transition ist der einzige
  // Zeitpunkt, der das deterministisch garantiert.
  const pendingAdvanceRef = useRef(null);

  const selectAdvanceProps = (fieldName) => ({
    MenuProps: {
      TransitionProps: {
        onExited: () => {
          if (pendingAdvanceRef.current !== fieldName) return;
          pendingAdvanceRef.current = null;
          focusNext(fieldName);
        },
      },
    },
  });

  const requestAdvance = (fieldName) => {
    pendingAdvanceRef.current = fieldName;
  };

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <Box
        {...containerProps}
        sx={{
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          overflowY: 'auto',
          flex: 1,
          ...focusVisibleSx,
        }}
      >
        {error && (
          // Der Schließen-Button bleibt aus der Tab-Kette: Im Telefonbetrieb
          // steuert ihn niemand an, Esc schließt das Panel ohnehin.
          <Alert
            severity="error"
            onClose={() => setError(null)}
            slotProps={{ closeButton: { tabIndex: -1 } }}
            sx={{ py: 0 }}
          >
            {error}
          </Alert>
        )}

        {/* Modus-Umschalter: kein Tab-Stopp, dafür Alt+S / Alt+V. */}
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
          fullWidth
          color="primary"
        >
          <ToggleButton value="sofort" tabIndex={-1}>Sofort</ToggleButton>
          <ToggleButton value="vorbestellung" tabIndex={-1}>Vorbestellung</ToggleButton>
        </ToggleButtonGroup>

        {mode === 'vorbestellung' && (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <DateTimePicker
              label="Datum und Uhrzeit"
              value={scheduledTime}
              onChange={(value) => setScheduledTime(value)}
              ampm={false}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  'data-nav': 'scheduledTime',
                  'data-nav-type': 'text',
                },
                // Datum wird getippt; das Kalender-Icon ist kein Tab-Stopp.
                openPickerButton: { tabIndex: -1 },
              }}
            />
          </LocalizationProvider>
        )}

        {/*
          freeSolo: Ein frei getippter Name muss möglich bleiben – ohne Eintrag
          entsteht ein Laufkunde, das ist der Normalweg. Die Vorschläge sind
          eine Tipphilfe, keine Pflichtauswahl.
        */}
        <Autocomplete
          freeSolo
          id={fieldId('quick-customer')}
          options={customers || []}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : customerName(option)
          }
          filterOptions={(options, { inputValue }) => {
            const term = inputValue.trim().toLowerCase();
            if (!term) return [];
            return options
              .filter((c) => {
                const nameMatch = customerName(c).toLowerCase().includes(term);
                const phoneMatch = customerPhone(c).toLowerCase().includes(term);
                return nameMatch || phoneMatch;
              })
              .slice(0, 8);
          }}
          inputValue={name}
          onInputChange={(_e, value, reason) => {
            setName(value);
            // Freies Tippen oder Leeren hebt die Zuordnung auf – sonst blieben
            // Vorlagen eines Kunden wählbar, dessen Name nicht mehr im Feld
            // steht. `reset` ist die Übernahme nach der Auswahl und zählt nicht.
            if (reason !== 'reset' && customerId) {
              setCustomerId(null);
              setTemplateId('');
            }
          }}
          onChange={(_e, option) => {
            if (!option || typeof option === 'string') {
              // Freitext bleibt Freitext: Laufkunde ohne Profil.
              setCustomerId(null);
              setTemplateId('');
              return;
            }
            setName(customerName(option));
            setCustomerId(option.id || null);
            setTemplateId('');
            // Telefonnummer nur übernehmen, wenn der Kunde eine hat – eine
            // bereits erfasste Nummer darf nicht verloren gehen.
            const phoneOfCustomer = customerPhone(option);
            if (phoneOfCustomer) setPhone(phoneOfCustomer);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {customerName(option)}
                </Typography>
                {customerPhone(option) && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {customerPhone(option)}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{ ...params.inputProps, autoComplete: NO_AUTOFILL }}
              data-nav="name"
              data-nav-type="text"
              autoFocus
              label="Kunde-Name"
              placeholder="Leer lassen für Laufkunde (Telefonkunde ohne Profil)"
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
          )}
        />
        <TextField
          data-nav="phone"
          data-nav-type="text"
          autoComplete={NO_AUTOFILL}
          label="Telefon"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={phoneRecognitionHint(phone).error}
          helperText={phoneRecognitionHint(phone).text || undefined}
          size="small"
          fullWidth
        />

        {customerTemplates.length > 0 && (
          <TextField
            select
            data-nav="template"
            data-nav-type="select"
            label="Aus Vorlage"
            value={templateId}
            onChange={(e) => {
              applyTemplate(e.target.value);
              requestAdvance('template');
            }}
            SelectProps={selectAdvanceProps('template')}
            size="small"
            fullWidth
            helperText="Gespeicherte Vorlagen dieses Kunden"
          >
            <MenuItem value="">
              <em>Keine</em>
            </MenuItem>
            {customerTemplates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.template_name || `${t.pickup_address} → ${t.destination_address}`}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Abholung → Zwischenstopps → Ziel, per Griff oder Alt+↑/↓ umsortierbar */}
        <RoutePointsEditor
          stopsCount={stops.length}
          onReorder={handleReorderRoutePoints}
          onRemoveStop={(index) => routeShortcuts.removeStop(index)}
          // Kompaktes „+" neben dem Zielfeld – im schmalen Panel spart das
          // die eigene Zeile. Kein Tab-Stopp (Regel 3); Tastatur: Alt+Z.
          destinationAction={(
            <Tooltip
              title={stops.length >= MAX_INTERMEDIATE_STOPS
                ? `Maximal ${MAX_INTERMEDIATE_STOPS} Zwischenstopps`
                : 'Zwischenstopp hinzufügen (Alt+Z)'}
            >
              <span>
                <IconButton
                  size="small"
                  tabIndex={-1}
                  color="primary"
                  aria-label="Zwischenstopp hinzufügen"
                  disabled={stops.length >= MAX_INTERMEDIATE_STOPS}
                  onClick={() => routeShortcuts.addStop()}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          renderField={(row) => {
            if (row.kind === 'pickup') {
              return (
                <PlaceAutocomplete
                  navName="pickupAddress"
                  subDestinationRole="pickup"
                  requireLocation
                  location={pickupLoc}
                  value={pickupAddress}
                  onChange={(e) => {
                    setPickupAddress(e.target.value);
                    if (e.target.value !== selectedAddressRef.current.pickup) setPickupLoc(null);
                  }}
                  onPlaceSelect={(addr, loc) => {
                    selectedAddressRef.current.pickup = addr;
                    setPickupAddress(addr);
                    setPickupLoc(loc);
                  }}
                  required
                  placeholder="Abholadresse *"
                  size="small"
                  fullWidth
                />
              );
            }
            if (row.kind === 'destination') {
              return (
                <PlaceAutocomplete
                  navName="destinationAddress"
                  subDestinationRole="destination"
                  requireLocation
                  location={destLoc}
                  value={destAddress}
                  onChange={(e) => {
                    setDestAddress(e.target.value);
                    if (e.target.value !== selectedAddressRef.current.destination) setDestLoc(null);
                  }}
                  onPlaceSelect={(addr, loc) => {
                    selectedAddressRef.current.destination = addr;
                    setDestAddress(addr);
                    setDestLoc(loc);
                  }}
                  placeholder="Zieladresse"
                  size="small"
                  fullWidth
                />
              );
            }
            const index = row.index;
            return (
              <PlaceAutocomplete
                navName={`stop-${index}`}
                subDestinationRole="stop"
                requireLocation
                location={stops[index]?.location}
                value={stops[index]?.address || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Getippter Text verwirft die Koordinaten; beim Anlegen wird
                  // nachgeocodiert (wie bei Abholung und Ziel).
                  setStops((prev) => prev.map((stop, i) => (
                    i === index ? { address: value, location: null } : stop
                  )));
                }}
                onPlaceSelect={(addr, loc) => setStops((prev) => prev.map((stop, i) => (
                  i === index ? { address: addr, location: loc } : stop
                )))}
                placeholder={`Zwischenstopp ${index + 1}`}
                size="small"
                fullWidth
              />
            );
          }}
        />
        <TextField
          select
          data-nav="vehicleType"
          data-nav-type="select"
          label="Fahrzeugtyp"
          value={vehicleType}
          onChange={(e) => {
            setVehicleType(e.target.value);
            requestAdvance('vehicleType');
          }}
          SelectProps={selectAdvanceProps('vehicleType')}
          size="small"
          fullWidth
          sx={{ mt: 1 }}
        >
          {VEHICLE_TYPE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        {/*
          Autocomplete statt Auswahlliste: Der Typeahead einer MUI-Auswahlliste
          springt nur auf den Anfangsbuchstaben und ist ab etwa 15 Fahrern
          unbrauchbar. Hier wird über den ganzen Namen gefiltert.
        */}
        <Autocomplete
          id={fieldId('quick-driver')}
          options={availableDrivers}
          getOptionLabel={(option) => driverLabel(option)}
          // Der Chip zeigt die Wagennummer – also muss sie sich auch tippen
          // lassen ("7" für Wagen 7), sonst führt die Anzeige in die Irre.
          filterOptions={(options, { inputValue }) => {
            const term = inputValue.trim().toLowerCase();
            if (!term) return options;
            return options.filter(
              (d) =>
                driverLabel(d).toLowerCase().includes(term) ||
                driverVehicleNumber(d).toLowerCase().includes(term)
            );
          }}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          value={availableDrivers.find((d) => d.id === driverId) || null}
          onChange={(_e, option) => setDriverId(option?.id || '')}
          renderOption={(props, option) => {
            const vehicleNumber = driverVehicleNumber(option);
            return (
              // Die Nummer steht rechtsbündig in einer Spalte: Der Disponent
              // sucht sie beim Überfliegen der Liste an einer festen Stelle.
              <li {...props} key={option.id}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {driverLabel(option)}
                  </Typography>
                  {vehicleNumber && (
                    <Chip
                      label={vehicleNumber}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
                    />
                  )}
                </Box>
              </li>
            );
          }}
          size="small"
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{ ...params.inputProps, autoComplete: NO_AUTOFILL }}
              data-nav="driver"
              data-nav-type="text"
              label="Zuweisung"
              placeholder="Fahrer (optional)"
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
              helperText={
                mode === 'sofort'
                  ? 'Nur Online-Fahrer mit Fahrzeug'
                  : 'Alle Fahrer wählbar'
              }
            />
          )}
        />

        <TextField
          data-nav="notes"
          data-nav-type="multiline"
          autoComplete={NO_AUTOFILL}
          label="Notiz"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
        />

        {/*
          "Fahrt erstellen" steht in der DOM- und damit Tab-Reihenfolge VOR
          "Abbrechen": Wer sich aus der Notiz weitertabbt und reflexartig Enter
          drückt, darf nicht die gerade erfasste Fahrt verwerfen. Die optische
          Anordnung (Abbrechen links) bleibt über CSS `order` erhalten.
        */}
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
          <Button
            data-nav="submit"
            data-nav-type="action"
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ order: 2 }}
          >
            Fahrt erstellen
          </Button>
          <Button
            data-nav="cancel"
            data-nav-type="action"
            variant="outlined"
            color="inherit"
            fullWidth
            onClick={() => onClose?.()}
            disabled={submitting}
            sx={{ order: 1 }}
          >
            Abbrechen
          </Button>
        </Box>

        <DispatchInputHelpDialog open={helpOpen} onClose={closeHelp} />
      </Box>
    </APIProvider>
  );
};

export default QuickRideForm;
